import axios from 'axios';
import { createAuthHeaders } from '../../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const edgeFunctionsApi = {
  /**
   * List deployed Edge Functions
   */
  async listFunctions(
    token: string,
    projectId: string,
  ): Promise<{ functions: any[] }> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/functions`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * List Edge Functions in workspace
   */
  async listWorkspaceFunctions(
    token: string,
    projectId: string,
  ): Promise<{ functions: any[] }> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/functions/workspace`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get function deployment status (workspace vs deployed)
   */
  async getFunctionStatus(token: string, projectId: string): Promise<any> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/functions/status`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Deploy function from workspace to OptiDev Cloud
   */
  async deployFromWorkspace(
    token: string,
    projectId: string,
    functionName: string,
  ): Promise<{ success: boolean; result: any }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/functions/deploy-from-workspace`,
      { functionName },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Delete an Edge Function
   */
  async deleteFunction(
    token: string,
    projectId: string,
    functionId: string,
  ): Promise<void> {
    await axios.delete(
      `${API_URL}/api/supabase/projects/${projectId}/functions/${functionId}`,
      {
        headers: createAuthHeaders(token),
      },
    );
  },

  /**
   * Test an Edge Function via backend proxy
   * Signs gateway JWT and calls Supabase directly
   */
  async testFunction(
    token: string,
    projectId: string,
    functionName: string,
    request: {
      method?: string;
      body?: unknown;
      headers?: Array<{ key: string; value: string }>;
      params?: Array<{ key: string; value: string }>;
    },
  ): Promise<{
    status: number;
    statusText: string;
    data: unknown;
    headers: Record<string, string>;
    duration: number;
  }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/functions/${functionName}/test`,
      request,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Create sample hello-world function in workspace
   */
  async createSampleFunction(
    token: string,
    projectId: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/functions/create-sample`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get function metrics (overview)
   */
  async getFunctionMetrics(
    token: string,
    projectId: string,
    interval?: '15min' | '1hr' | '3hr' | '1day',
    functionName?: string,
  ): Promise<{
    totalInvocations: number;
    avgResponseTime: number;
    successRate: number;
    logCount: number;
    successCount: number;
    errorCount: number;
    invocationsByFunction: Record<string, number>;
    timeSeries: Array<{ timestamp: string; count: number }>;
  }> {
    const params = new URLSearchParams();
    if (interval) {
      params.append('interval', interval);
    }
    if (functionName) {
      params.append('functionName', functionName);
    }
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/functions/metrics?${params.toString()}`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get function invocations (HTTP request/response history)
   */
  async getFunctionInvocations(
    token: string,
    projectId: string,
    filters?: {
      functionName?: string;
      status?: number;
      limit?: number;
      offset?: number;
      interval?: '15min' | '1hr' | '3hr' | '1day';
    },
  ): Promise<{
    invocations: Array<{
      id: string;
      timestamp: Date;
      functionName: string;
      method: string;
      status: number;
      duration: number;
      path?: string;
      requestHeaders?: Record<string, string>;
      error?: string;
      metadata?: any;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.functionName) params.append('functionName', filters.functionName);
    if (filters?.status) params.append('status', filters.status.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    if (filters?.interval) params.append('interval', filters.interval);

    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/functions/invocations${params.toString() ? `?${params.toString()}` : ''}`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get function console logs
   */
  async getFunctionLogs(
    token: string,
    projectId: string,
    filters?: {
      functionId?: string; // UUID of the function to filter by
      functionName?: string; // Deprecated: use functionId instead
      level?: string; // Accept any log level (log, info, warn, error, etc.)
      limit?: number;
      interval?: '15min' | '1hr' | '3hr' | '1day';
    },
  ): Promise<{
    logs: Array<{
      id: string;
      timestamp: Date;
      level: string;
      message: string;
      functionName: string;
      metadata?: any[];
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.functionId) params.append('functionId', filters.functionId);
    if (filters?.functionName) params.append('functionName', filters.functionName);
    if (filters?.level) params.append('level', filters.level);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.interval) params.append('interval', filters.interval);

    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/functions/logs${params.toString() ? `?${params.toString()}` : ''}`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },
};
